import { BigNumber, BigNumberish } from '@ethersproject/bignumber'
import { BytesLike } from '@ethersproject/bytes'
import { useCallback } from 'react'
import { TransactionInfo } from '../providers/transactions/model'
import { useEthers } from './useEthers'
import { useTransactions } from './useTransactions'
import { useGasPrice } from '../providers'
import { providers } from 'ethers'
import { GAS_LIMIT_SAFETY_OFFSET, MAX_DEFAULT_GAS_LIMIT } from '../constants'

const EIP_1559_TX_TYPE = 2

export interface Transaction {
  to?: string
  gasLimit?: BigNumberish
  data?: BytesLike
  value?: BigNumberish
  txInfo: TransactionInfo
  maxFeePerGas?: BigNumber
  maxPriorityFeePerGas?: BigNumber
}

export function useSendTransaction() {
  const { library, account, chainId } = useEthers()
  const { addTransaction } = useTransactions()
  const gasPrice = useGasPrice()

  const sendTransaction = useCallback(
    async function ({ txInfo, ...transaction }: Transaction) {
      if (!library || !account || !chainId) {
        throw new Error('Provider not initialized')
      }
      if (!gasPrice || !gasPrice.networkGasTip || !gasPrice.maxGasFee) {
        throw new Error('Gas price not initialized')
      }
      const signer = library.getSigner()
      const gasLimit = await estimateGasLimit(library, transaction)
      const tx = await signer.sendTransaction({
        ...transaction,
        maxFeePerGas: transaction.maxFeePerGas || gasPrice.maxGasFee,
        maxPriorityFeePerGas: transaction.maxPriorityFeePerGas || gasPrice.networkGasTip,
        type: EIP_1559_TX_TYPE,
        chainId,
        gasLimit: gasLimit.add(GAS_LIMIT_SAFETY_OFFSET),
      })
      addTransaction({ chainId, from: account, hash: tx.hash, nonce: tx.nonce, txInfo })
    },
    [library, account, chainId]
  )

  return sendTransaction
}

async function estimateGasLimit(library: providers.Web3Provider, transaction: Omit<Transaction, 'txInfo'>) {
  try {
    return await library.getSigner().estimateGas(transaction)
  } catch (err) {
    // Gnosis Safe error
    return await estimateGasLimitWithProvider(library, transaction)
  }
}

async function estimateGasLimitWithProvider(library: providers.Web3Provider, transaction: Omit<Transaction, 'txInfo'>) {
  try {
    return await library.estimateGas(transaction)
  } catch (err) {
    // Gnosis Safe token send error
    return BigNumber.from(MAX_DEFAULT_GAS_LIMIT)
  }
}
